import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchBprsAssessment } from "../../../store/slices/bprsAssessmentsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function BPRSFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentBprsAssessment, isLoading, error } = useSelector((state) => state.bprsAssessments || {});

    useEffect(() => {
        if (id) dispatch(fetchBprsAssessment(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentBprsAssessment) {
            const timer = setTimeout(() => window.print(), 1000);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentBprsAssessment, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading BPRS Report...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentBprsAssessment) return <div className="p-8 text-center">No record found.</div>;

    const d = currentBprsAssessment;

    // Direct mapping to your backend snake_case response keys
    const bprsItems = [
        { title: "Somatic Concern", scoreKey: "somatic_concern_score", valueKey: "somatic_concern_value" },
        { title: "Anxiety", scoreKey: "anxiety_score", valueKey: "anxiety_value" },
        { title: "Emotional Withdrawal", scoreKey: "emotional_withdrawal_score", valueKey: "emotional_withdrawal_value" },
        { title: "Conceptual Disorganization", scoreKey: "conceptual_disorganization_score", valueKey: "conceptual_disorganization_value" },
        { title: "Guilt Feelings", scoreKey: "guilt_feelings_score", valueKey: "guilt_feelings_value" },
        { title: "Tension", scoreKey: "tension_score", valueKey: "tension_value" },
        { title: "Mannerisms and Posturing", scoreKey: "mannerisms_posturing_score", valueKey: "mannerisms_posturing_value" },
        { title: "Grandiosity", scoreKey: "grandiosity_score", valueKey: "grandiosity_value" },
        { title: "Depressive Mood", scoreKey: "depressive_mood_score", valueKey: "depressive_mood_value" },
        { title: "Hostility", scoreKey: "hostility_score", valueKey: "hostility_value" },
        { title: "Suspiciousness", scoreKey: "suspiciousness_score", valueKey: "suspiciousness_value" },
        { title: "Hallucinatory Behavior", scoreKey: "hallucinatory_behavior_score", valueKey: "hallucinatory_behavior_value" },
        { title: "Motor Retardation", scoreKey: "motor_retardation_score", valueKey: "motor_retardation_value" },
        { title: "Uncooperativeness", scoreKey: "uncooperativeness_score", valueKey: "uncooperativeness_value" },
        { title: "Unusual Thought Content", scoreKey: "unusual_thought_content_score", valueKey: "unusual_thought_content_value" },
        { title: "Blunted Affect", scoreKey: "blunted_affect_score", valueKey: "blunted_affect_value" },
        { title: "Excitement", scoreKey: "excitement_score", valueKey: "excitement_value" },
        { title: "Disorientation", scoreKey: "disorientation_score", valueKey: "disorientation_value" }
    ];

    const totalScore = bprsItems.reduce((sum, item) => sum + (parseInt(d[item.scoreKey] || 0, 10)), 0);

    return (
        <>
            <style jsx global>{`
                * { box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 11px !important; margin: 0 !important; padding: 0 !important; }
                .print-container { padding: 30px !important; position: relative !important; }
                ${printFooterStyles}
                .logo { position: absolute !important; top: 15px !important; left: 15px !important; }
                .logo img { height: 60px !important; width: auto !important; }
                .header { text-align: center !important; margin-bottom: 20px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.5rem !important; font-weight: bold !important; }
                .meta-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 20px !important; }
                .meta-table td { border: 1px solid #000 !important; padding: 6px !important; }
                .label-bg { background: #f2f2f2 !important; font-weight: bold !important; width: 18% !important; }
                .bprs-table { width: 100% !important; border-collapse: collapse !important; margin-bottom: 60px !important; }
                .bprs-table th, .bprs-table td { border: 1px solid #000 !important; padding: 6px 10px !important; }
                .bprs-table th { background: #eee !important; font-weight: bold !important; text-align: center !important; }
                .score-col { width: 60px !important; text-align: center !important; font-weight: bold !important; font-size: 13px !important; }
                .val-col { width: 150px !important; text-align: center !important; }
                .total-box { margin-top: 20px !important; border: 2px solid #000 !important; padding: 15px !important; background: #f9f9f9 !important; display: flex !important; justify-content: space-between !important; align-items: center !important; page-break-inside: avoid !important; }
                .total-text { font-size: 1.4rem !important; font-weight: bold !important; }
                .footer { margin-top: 30px !important; border-top: 1px solid #000 !important; padding-top: 10px !important; text-align: center !important; font-size: 10px !important; }
                .sig-row { display: flex !important; justify-content: space-between !important; margin-top: 40px !important; }
                .sig-box { border-top: 1px solid #000 !important; width: 220px !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
                @media print { .bprs-table tr { page-break-inside: avoid !important; } }
            `}</style>

            <PrintWrapper>
                <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>BRIEF PSYCHIATRIC RATING SCALE (BPRS) REPORT</h6>
                </div>

                <table className="meta-table">
                    <tbody>
                        <tr>
                            <td className="label-bg">Client Name</td>
                            <td>{d.client_name}</td>
                            <td className="label-bg">Client ID#</td>
                            <td>{d.client_id}</td>
                        </tr>
                        <tr>
                            <td className="label-bg">Date</td>
                            <td>{new Date(d.created_at).toLocaleDateString('en-GB')}</td>
                            <td className="label-bg">MD</td>
                            <td>{d.md}</td>
                        </tr>
                    </tbody>
                </table>

                <table className="bprs-table">
                    <thead>
                        <tr>
                            <th style={{ width: '40px' }}>S#</th>
                            <th>Clinical Symptom / Observation</th>
                            <th className="val-col">Response Level</th>
                            <th className="score-col">Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bprsItems.map((item, index) => (
                            <tr key={index}>
                                <td style={{ textAlign: 'center' }}>{index + 1}</td>
                                <td style={{ fontWeight: 'bold' }}>{item.title}</td>
                                <td className="val-col">{d[item.valueKey]}</td>
                                <td className="score-col">{d[item.scoreKey]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* <div className="total-box">
                    <div>
                        <div className="total-text">CUMULATIVE TOTAL SCORE: {totalScore}</div>
                        <div style={{ fontSize: '10px', color: '#555' }}>Range: 18 - 126 | Higher scores indicate greater severity</div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '10px' }}>
                        Note: 0 = Not Assessed, 1 = Not Present
                    </div>
                </div> */}

                <div className="sig-row">
                    <div className="sig-box">Patient / Guardian Signature</div>
                    <div className="sig-box">Assessing Practitioner Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
            </PrintWrapper>
        </>
    );
}